import { fileUpload, nameChangeApiCall } from '@api/form-verification';
import DynamicInputField from '@components/dynamic-input-field';
import { tawkToConfig } from '@config/addn-configs';
import { emailTemplate } from '@global/helpers/general';
import { mixpanel, trackingEvents } from '@global/monitoring/mixpanel';
import { Box, Button, Dialog, DialogContent, Divider, Grid, Typography } from '@mui/material';
import { RootState } from '@redux/store';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { HelperTextJSXProps } from '../helper-text-jsx';

const NameAsPerCertificate = (props: HelperTextJSXProps) => {
  const { input, value } = props;
  const [open, setOpen] = useState(false);
  const [nameChangeRequestSubmitted, setNameChangeRequestSubmitted] = useState(false);
  const profile = useSelector((s: RootState) => s.profile);
  const { txnInfo } = useSelector((s: RootState) => s.formData);
  return (
    <>
      <Box component="span" sx={{ color: '#666666', fontWeight: '400' }}>
        {txnInfo?.nameChangeRequest?.requested || nameChangeRequestSubmitted
          ? 'Name change document submitted successfully. '
          : input?.getProfileValue &&
            eval(input?.getProfileValue)(profile) === value &&
            'This name has been sourced from your profile. '}
        {'If you require any changes, kindly '}
        <span
          style={{
            color: '#044da1',
            textDecoration: 'none',
            cursor: 'pointer',
            fontWeight: '600'
          }}
          onClick={() => {
            if (txnInfo?.nameChangeRequest?.requested || nameChangeRequestSubmitted) {
              mixpanel.track(trackingEvents.verification.events.enquireToEditNameOnTheCertificate);
              if (tawkToConfig()) {
                window?.tawkToReactElement?.maximize();
                window?.$_Tawk?.maximize();
              } else {
                window.open(emailTemplate(import.meta.env.VITE_ORG_MAIL), '_blank');
              }
            } else {
              mixpanel.track(trackingEvents.nameChange.events.initiated, {
                trigger: 'preference'
              });
              setOpen(true);
            }
          }}>
          Click Here{' '}
        </span>
        <NameChangeDialog
          open={open}
          setOpen={setOpen}
          setNameChangeRequestSubmitted={setNameChangeRequestSubmitted}
        />
      </Box>
    </>
  );
};
const NameChangeDialog = ({
  open,
  setOpen,
  setNameChangeRequestSubmitted
}: {
  open: boolean,
  setOpen: any,
  setNameChangeRequestSubmitted: any
}) => {
  const { searchedQuery, currentVerification } = useSelector((s: RootState) => s.misc);
  const [step, setStep] = useState<number>(0);
  const [input, setInput] = useState<{ [key: string]: string | File | null }>({
    document: null
  });
  useEffect(() => {
    if (!open) {
      setStep(0);
      setInput({ document: null });
      setError({});
    }
  }, [open]);

  const handleSubmit = async () => {
    let errors = {};
    nameChangeInputs?.forEach((item: any) => {
      if (!eval(item?.validation?.fn)(input[item.fieldName])) {
        errors = { ...errors, [item.fieldName]: item.validation.message };
      }
      setError(errors);
    });
    if (Object.keys(errors).length > 0) {
      return null;
    }
    const file = input.document as File;
    const res = await fileUpload(file);
    if (res?.data.fileUrl) {
      mixpanel.track(trackingEvents.nameChange.events.documentUploaded, {
        file_format: file?.type,
        file_size_kb: file ? Math.round(file.size / 1024) : null,
        document_type: 'other'
      });
      await nameChangeApiCall({
        orgId: searchedQuery?.orgId,
        txnId: searchedQuery?.txnId,
        verificationName: currentVerification,
        documentList: [{ label: 'name change request', url: res?.data.fileUrl }]
      }).then((nameChangeRes: any) => {
        if (nameChangeRes) {
          mixpanel.track(trackingEvents.nameChange.events.submitted, {
            name_changed: true,
            document_type: 'other'
          });
          setNameChangeRequestSubmitted();
        }
      });
    }
    setOpen(false);
  };

  const [error, setError] = useState<{ [key: string]: string }>({});
  return (
    <Dialog
      maxWidth="xs"
      fullWidth
      open={open}
      onClose={() => {
        setOpen(false);
      }}
      sx={{ padding: '1em' }}>
      <DialogContent
        sx={{
          padding: '2em',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
        <Box
          sx={{
            borderRadius: '50%',
            padding: '0em',
            backgroundColor: '#FB8B24',
            color: '#ffffff',
            fontWeight: 600,
            fontSize: '4rem',
            height: '60px',
            width: '60px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
          ?
        </Box>
        <Typography variant="h1" sx={{ margin: '1em 0 0.5em' }}>
          Facing issues with your name?
        </Typography>
        {step === 0 ? (
          <Grid>
            <Typography variant="body1" sx={{ lineHeight: '1.2em' }}>
              The following minor differences are acceptable and can be ignored:
              <ul style={{ marginLeft: '2em' }}>
                <li>Missing/additional middle name</li>
                <li>Spelling mismatch (such as Pravin, Praveen)</li>
                <li>Swapped first and last name (such as Raman Singh, Singh Raman)</li>
              </ul>{' '}
              <Divider sx={{ margin: '1em 0' }} />
              You can request to change the name if you have changed your name recently,
              <br />
              <br />
              or if your name is different in the provided document and government ID.
            </Typography>
            <br />
            <Button
              variant="contained"
              color="primary"
              onClick={() => setStep(1)}
              sx={{ marginTop: '1em' }}>
              Request for name change
            </Button>
            <Button variant="text" onClick={() => setOpen(false)} sx={{ marginTop: '1em' }}>
              Continue without changes
            </Button>
          </Grid>
        ) : (
          <Grid sx={{ width: '100%' }}>
            {nameChangeInputs.map((item: any, i) => (
              <DynamicInputField
                input={item}
                fieldIdx={i}
                handleChange={({ name, value }: { name: string, value: string | File | null }) =>
                  setInput((inputs: any) => {
                    const newInputs = { ...inputs };
                    newInputs[name] = value;
                    return newInputs;
                  })
                }
                value={input[item?.fieldName]}
                error={Boolean(error[item?.fieldName])} //error bool
                helperText={error[item?.fieldName]} //error bool
                sx={{ margin: '1em 0' }}
              />
            ))}
            <Typography variant="body2" sx={{ m: '1em 0' }}>
              Accepted documents (Name Change Affidavit, Marriage Certificate, etc.)
            </Typography>
            <Button variant="contained" color="primary" onClick={handleSubmit}>
              Confirm
            </Button>
            <Button variant="text" onClick={() => setStep(0)} sx={{ marginTop: '1em' }}>
              Cancel
            </Button>
            <Typography
              variant="body1"
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                margin: '1em'
              }}>
              Still facing issues? &nbsp;
              <span
                style={{
                  color: '#044da1',
                  textDecoration: 'none',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
                onClick={() => {
                  mixpanel.track(
                    trackingEvents.verification.events.enquireToEditNameOnTheCertificate
                  );
                  if (tawkToConfig()) {
                    window?.tawkToReactElement?.maximize();
                    window?.$_Tawk?.maximize();
                  } else {
                    window.open(emailTemplate(import.meta.env.VITE_ORG_MAIL), '_blank');
                  }
                }}>
                Contact Support
              </span>
            </Typography>
          </Grid>
        )}
      </DialogContent>
    </Dialog>
  );
};

const nameChangeInputs = [
  // {
  //   type: 'field',
  //   inputType: 'field',
  //   label: 'Enter name',
  //   validation: {
  //     fn: '(e) => { return e?.length > 0; }',
  //     message: `Name should not be empty`
  //   },
  //   required: true,
  //   placeholder: '',
  //   fieldName: 'name',
  //   value: '',
  //   isEditable: true
  // },
  {
    type: 'file',
    inputType: 'file',
    label: 'Upload documents',
    validation: {
      fn: '(e) => { return e; }',
      message: `Upload document proof`
    },
    required: true,
    placeholder: '',
    fieldName: 'document',
    value: '',
    isEditable: true,
    helperText: 'like (Name Change Affidavit, Marriage Certificate, etc.)'
  }
];
export default NameAsPerCertificate;
